import { useState, useEffect } from 'react';
import { useSettings } from '../SettingsContext';

/**
 * Normalise an images array whose items may have been double-encoded by the
 * LLM (each object serialised as a JSON string inside the outer array).
 *
 * Input:  [{ title: "..." }, "{\"title\": \"...\"}", ...]
 * Output: [{ title: "..." }, { title: "..." }, ...]
 */
function normaliseItems(items) {
  return items.map(item => {
    if (typeof item === 'string') {
      try { return JSON.parse(item); } catch { return null; }
    }
    return item;
  }).filter(Boolean);
}

function extractImages(parsed) {
  // Shape 1 (expected): { type, query, images: [...] }
  if (parsed && Array.isArray(parsed.images)) {
    return { images: normaliseItems(parsed.images), query: parsed.query || '' };
  }

  // Shape 2: bare array
  if (Array.isArray(parsed)) {
    return { images: normaliseItems(parsed), query: '' };
  }

  // Shape 3: { hits: [...] } (raw Pixabay leak)
  if (parsed && Array.isArray(parsed.hits)) {
    return {
      query: parsed.query || '',
      images: parsed.hits.map(item => ({
        title:         item.tags || '',
        thumbnail_url: item.previewURL || '',
        image_url:     item.largeImageURL || item.webformatURL || '',
        source_url:    item.pageURL || '',
      })).filter(i => i.thumbnail_url),
    };
  }

  return null;
}

/**
 * Clip a string to the last closing brace or bracket, discarding any
 * trailing text the LLM may have appended after the JSON payload.
 */
function clipToJsonEnd(s) {
  const last = Math.max(s.lastIndexOf('}'), s.lastIndexOf(']'));
  return last !== -1 ? s.slice(0, last + 1) : s;
}

/**
 * Sanitise the raw string before JSON.parse.
 *
 * Handles several malformed shapes the LLM can emit:
 *
 * 1. Full fence leaks through as a single text segment:
 *      ```images\n{...}\n```  →  strip wrapper
 *
 * 2. Language tag with whitespace (existing case):
 *      "images {..."          →  strip "images "
 *
 * 3. Language tag WITHOUT whitespace (new fix):
 *      "images{..."           →  strip "images" when next char is { or [
 *
 * 4. Nested ```json fence inside the images fence:
 *      "```json\n{...}\n```"  →  strip inner fence
 *
 * 5. Trailing text after the closing } / ]:
 *      "{...}\nHere are some images."  →  clip to last } / ]
 */
function sanitiseRaw(raw) {
  if (typeof raw !== 'string') return raw;
  let s = raw.trim();

  // Strip an entire ```images ... ``` or ```chart ... ``` wrapper if present
  const fenceMatch = s.match(/^```(?:images|chart)[ \t]*\r?\n?([\s\S]*?)```$/);
  if (fenceMatch) {
    s = fenceMatch[1].trim();
  }

  // Strip an inner ```json ... ``` wrapper that the LLM sometimes nests inside
  const innerFence = s.match(/^```(?:json)?[ \t]*\r?\n?([\s\S]*?)```$/);
  if (innerFence) {
    s = innerFence[1].trim();
  }

  // Strip a bare leading language word left by the fence parser.
  // OLD: /^(images|chart)\s+/i  — only matched when whitespace followed
  // NEW: also match when the very next char is { or [ (no whitespace)
  s = s.replace(/^(images|chart)([\s{[])/i, (_, _word, nextChar) => nextChar);

  // Clip anything after the final } or ] (trailing LLM commentary)
  s = clipToJsonEnd(s);

  return s;
}

export default function ImageGrid({ raw }) {
  const { imageSearchPageSize } = useSettings();
  const pageSize = imageSearchPageSize || 10;

  const [visibleCount, setVisibleCount] = useState(pageSize);

  // Reset pagination whenever a new result comes in or page size changes
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [raw, pageSize]);

  const cleaned = sanitiseRaw(raw);

  let parsed;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    return (
      <div className="image-grid-error">
        <pre style={{ fontSize: 10, marginTop: 6, opacity: 0.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{raw}</pre>
      </div>
    );
  }

  const extracted = extractImages(parsed);

  if (!extracted || !extracted.images.length) {
    return (
      <div className="image-grid-error">
        No images found{parsed.query ? ` for "${parsed.query}"` : ''}.
      </div>
    );
  }

  const { images, query } = extracted;
  const shownCount = Math.min(visibleCount, images.length);
  const visible    = images.slice(0, shownCount);
  const remaining  = images.length - shownCount;
  const nextBatch  = Math.min(remaining, pageSize);

  return (
    <div className="image-grid-block">
      {query && (
        <div className="image-grid-header">
          <span className="image-grid-title">Images for "{query}"</span>
          <span className="image-grid-count">
            {shownCount} of {images.length}
          </span>
        </div>
      )}

      <div className="image-grid">
        {visible.map((img, i) => (
          <a
            key={i}
            href={img.source_url || img.image_url}
            target="_blank"
            rel="noopener noreferrer"
            className="image-grid-item"
            title={img.title}
          >
            <img
              src={img.thumbnail_url}
              alt={img.title}
              className="image-grid-thumb"
              loading="lazy"
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
          </a>
        ))}
      </div>

      {remaining > 0 && (
        <button
          className="image-grid-more"
          onClick={() => setVisibleCount(c => c + pageSize)}
        >
          More → ({nextBatch} more, {remaining} remaining)
        </button>
      )}
    </div>
  );
}